'use client';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

// Pointer-events drag machine, ported from the approved prototype: pointer
// capture on the source element, 8px activation threshold (below = tap), a
// floating clone that follows the pointer, elementFromPoint hit-testing
// against [data-gp-cell] targets, and a drop-glow on the hovered open cell.
// One code path for mouse and touch (source elements set touch-action: none).

export interface DragView {
  carId: string;
  fromCell: number | null;
  x: number;
  y: number;
  hoverCell: number | null;
}

interface DragInternal {
  carId: string;
  fromCell: number | null;
  sx: number;
  sy: number;
  active: boolean;
}

const ACTIVATE_PX = 8;

function cellAt(x: number, y: number): number | null {
  const el = document.elementFromPoint(x, y);
  const cell = el?.closest?.('[data-gp-cell]');
  if (!cell) return null;
  const idx = Number((cell as HTMLElement).dataset.gpCell);
  return Number.isInteger(idx) ? idx : null;
}

export function useGridDrag({
  isCellOpen,
  onTapCard,
  onDrop,
}: {
  /** Can a drag land here? (empty cell, game not over) */
  isCellOpen: (cell: number) => boolean;
  /** Pointer went down and up without real movement on a lot card. */
  onTapCard: (carId: string) => void;
  /** Drag ended: target is an open cell index, or null for off-grid. */
  onDrop: (carId: string, fromCell: number | null, target: number | null) => void;
}) {
  const ref = useRef<DragInternal | null>(null);
  const [view, setView] = useState<DragView | null>(null);

  function start(e: ReactPointerEvent, carId: string, fromCell: number | null) {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    ref.current = { carId, fromCell, sx: e.clientX, sy: e.clientY, active: false };
  }

  function move(e: ReactPointerEvent) {
    const d = ref.current;
    if (!d) return;
    if (!d.active) {
      if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < ACTIVATE_PX) return;
      d.active = true;
    }
    e.preventDefault();
    const at = cellAt(e.clientX, e.clientY);
    const hoverCell = at !== null && at !== d.fromCell && isCellOpen(at) ? at : null;
    setView({ carId: d.carId, fromCell: d.fromCell, x: e.clientX, y: e.clientY, hoverCell });
  }

  function end(e: ReactPointerEvent) {
    const d = ref.current;
    if (!d) return;
    ref.current = null;
    setView(null);
    if (!d.active) {
      if (d.fromCell === null) onTapCard(d.carId); // tap on a parked car: no-op
      return;
    }
    const at = cellAt(e.clientX, e.clientY);
    if (at !== null) {
      if (at === d.fromCell) return; // dropped back home
      if (!isCellOpen(at)) return; // occupied slot: nothing happens
      onDrop(d.carId, d.fromCell, at);
      return;
    }
    onDrop(d.carId, d.fromCell, null); // off-grid: discard (controller decides)
  }

  function cancel() {
    ref.current = null;
    setView(null);
  }

  /** Spread onto a draggable source (lot card or filled cell). */
  function dragProps(carId: string, fromCell: number | null) {
    return {
      onPointerDown: (e: ReactPointerEvent) => {
        if ((e.target as HTMLElement).closest?.('.gp-rm')) return; // × button wins
        e.preventDefault();
        start(e, carId, fromCell);
      },
      onPointerMove: move,
      onPointerUp: end,
      onPointerCancel: cancel,
    };
  }

  return { drag: view, dragProps };
}
